import { useLazyQuery } from "@apollo/client/react";
import { CloseOutlined, ImportOutlined, RightOutlined, SearchOutlined } from "@ant-design/icons";
import { cx } from "@linaria/core";
import { Button, Input, Space, Table } from "antd";
import { type KeyboardEvent, type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { GenericIngredientDetails } from "../../../components/GenericIngredientDetails.tsx";
import { TableEmptyState } from "../../../components/TableEmptyState.tsx";
import { genericIngredientDisplayName } from "../../../utils/genericIngredient.ts";
import {
  type GenericIngredientFragment,
  SearchGenericIngredientsDocument,
} from "../../../gql/generated/graphqlSchema.tsx";
import { useDebouncedSearch } from "../../../hooks/useDebouncedSearch.ts";
import { clickable } from "../../../styles/shared.styles.ts";
import {
  chevronCell,
  container,
  footerRow,
  leftPane,
  rightPane,
  searchRow,
  splitArea,
} from "../../../styles/splitPaneSearch.styles.ts";

const pageSize = 10;

const defaultActionIcon = <ImportOutlined />;
const defaultCancelIcon = <CloseOutlined />;

export const GenericIngredientSearch = ({
  actionLabel,
  actionIcon = defaultActionIcon,
  cancelLabel = undefined,
  cancelIcon = defaultCancelIcon,
  onCancel,
  onSelect,
}: {
  readonly actionLabel: string;
  readonly actionIcon?: ReactNode;
  readonly cancelLabel?: string;
  readonly cancelIcon?: ReactNode;
  readonly onCancel: () => void;
  readonly onSelect: (gi: GenericIngredientFragment) => void;
}) => {
  const { t, i18n } = useTranslation();
  const [searchGenericIngredients, { data, previousData, loading, called }] = useLazyQuery(
    SearchGenericIngredientsDocument,
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const searchResult = (data ?? previousData)?.searchGenericIngredients;
  const results = searchResult?.ingredients ?? [];
  const total = searchResult?.total ?? 0;

  // `selectedIndex` is the user's explicit pick; when null (fresh results) the
  // effective selection falls back to row 0.
  const effectiveIndex =
    selectedIndex != null && selectedIndex < results.length ? selectedIndex : results.length > 0 ? 0 : null;
  const selected = effectiveIndex != null ? results[effectiveIndex] : null;

  const fireSearch = useCallback(
    (query: string, offset: number) => {
      void searchGenericIngredients({
        variables: {
          query,
          offset,
          limit: pageSize,
          language: i18n.language,
        },
      });
    },
    [i18n.language, searchGenericIngredients],
  );

  useEffect(() => {
    fireSearch("", 0);
  }, [fireSearch]);

  const { debounce: debouncedFireSearch } = useDebouncedSearch((value) => fireSearch(value, 0));

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPage(1);
    setSelectedIndex(null);
    debouncedFireSearch(value);
  };

  const scrollToIndex = (index: number) => {
    const row = listRef.current?.querySelectorAll(".ant-table-row")[index];
    row?.scrollIntoView({ block: "nearest" });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (results.length === 0) return;
    const current = effectiveIndex ?? 0;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = Math.min(current + 1, results.length - 1);
      setSelectedIndex(next);
      scrollToIndex(next);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const next = Math.max(current - 1, 0);
      setSelectedIndex(next);
      scrollToIndex(next);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (selected) onSelect(selected);
    }
  };

  const handleConfirm = useCallback(() => {
    if (selected) onSelect(selected);
  }, [onSelect, selected]);

  return (
    <div className={container}>
      <div className={searchRow}>
        <Input
          allowClear
          autoFocus
          onChange={(e) => handleSearchChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t("ingredients.nutrition.searchGenericIngredient")}
          prefix={<SearchOutlined />}
          size="large"
          value={searchQuery}
        />
      </div>

      <div className={splitArea}>
        <div className={leftPane} ref={listRef}>
          {called ? (
            <Table<GenericIngredientFragment>
              columns={[
                {
                  title: t("ingredients.name"),
                  key: "name",
                  ellipsis: { showTitle: true },
                  render: (_, record) => genericIngredientDisplayName(record, i18n.language),
                },
                {
                  key: "chevron",
                  width: 32,
                  className: chevronCell,
                  render: () => <RightOutlined />,
                },
              ]}
              dataSource={results}
              loading={loading}
              locale={{
                emptyText: (
                  <TableEmptyState
                    emptyDescription={t("ingredients.nutrition.searchNoResults", { query: searchQuery })}
                    loading={loading}
                  />
                ),
              }}
              onChange={(pag) => {
                const newPage = pag.current ?? 1;
                setPage(newPage);
                setSelectedIndex(null);
                fireSearch(searchQuery, (newPage - 1) * pageSize);
              }}
              onRow={(record, index) => ({
                onClick: () => setSelectedIndex(index ?? null),
                onDoubleClick: () => onSelect(record),
                className: cx(clickable, index === effectiveIndex && "ant-table-row-selected"),
              })}
              pagination={{
                current: page,
                pageSize,
                total,
                showSizeChanger: false,
                showTotal: (count) => (count > 0 ? t("ingredients.nutrition.searchResultsCount", { count }) : null),
              }}
              rowKey="id"
              showHeader={false}
              size="small"
              tableLayout="fixed"
            />
          ) : null}
        </div>

        {selected != null ? (
          <div className={rightPane}>
            <GenericIngredientDetails ingredient={selected} />
          </div>
        ) : null}
      </div>

      <div className={footerRow}>
        <Space wrap>
          <Button icon={cancelIcon} onClick={onCancel}>
            {cancelLabel ?? t("common.cancel")}
          </Button>
          <Button color="primary" disabled={selected == null} icon={actionIcon} onClick={handleConfirm} variant="solid">
            {actionLabel}
          </Button>
        </Space>
      </div>
    </div>
  );
};
